import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Pill, Search, Eye, Calendar, User, Package, Flag, ChevronUp, ChevronDown, ChevronsUpDown, XCircle, Clock, MoreHorizontal, CreditCard, Send, CheckCircle, AlertCircle, Pencil, ShieldCheck } from 'lucide-react';
import RxSafetyDialog from './intellihealth/RxSafetyDialog';
import { Patient } from '@/lib/types';
import { prescriptionService } from '@/services/prescriptionService';
import { Prescription } from '@/lib/types';
import { Input } from '../ui/input';
import { useQuery } from '@tanstack/react-query';
import PrescriptionDetailSheet from '../clinic/PrescriptionDetailSheet';
import { formatToLocalDate, getDefaultContactValue } from '@/lib/utils';
import SmartPagination from '../ui/smart-pagination';
import { medicationUnits } from '@/lib/medication-unit';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import PaymentSheet from '../clinic/PaymentSheet';
import SendPaymentLinkDialog from '../dialog/SendPaymentLinkDialog';
import CancelPrescriptionDialog from '../clinic/Prescriptions/CancelPrescriptionDialog';
import PrescriptionFormSheet from '../clinic/PrescriptionFormSheet';
import VerificationConfirmationDialog from '@/components/dialog/verification-confirmation-dialog';
import { useClinic } from '@/contexts/ClinicContext';
import { permissionCheck } from '@/lib/permission-check';
import { toast } from '@/hooks/use-toast';

interface PatientPrescriptionsTabProps {
  patientId: string;
}

const PatientPrescriptionsTab = ({ patientId }: PatientPrescriptionsTabProps) => {
  const { isPractitioner, isAdmin, isClinicStaff, rolePermissions } = useClinic();
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [safetyDraft, setSafetyDraft] = useState<{
    medication?: string;
    dose?: string;
    quantity?: string;
    refills?: string;
  }>({});
  const safetyPatient = patientId ? ({ id: patientId } as Patient) : null;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isPaymentSheetOpen, setIsPaymentSheetOpen] = useState(false);
  const [isVerificationConfirmationOpen, setIsVerificationConfirmationOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isEditPrescriptionOpen, setIsEditPrescriptionOpen] = useState(false);
  const [isSendLinkSheetOpen, setIsSendLinkSheetOpen] = useState<{
    open: boolean;
    prescription: Prescription | null;
  }>({ open: false, prescription: null });
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isVerificationProcessing, setIsVerificationProcessing] = useState(false);


  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-slate-400" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-3 w-3 text-slate-700" />
    ) : (
      <ChevronDown className="h-3 w-3 text-slate-700" />
    );
  };

  const getPriorityText = (priority: number) => {
    switch (priority) {
      case -1:
        return 'Low';
      case 1:
        return 'Normal';
      case 2:
        return 'Elevated';
      case 3:
        return 'Urgent';
      default:
        return 'Normal';
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case -1:
        return 'bg-slate-100 text-slate-600';
      case 1:
        return 'bg-green-100 text-green-700';
      case 2:
        return 'bg-amber-100 text-amber-700';
      case 3:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-green-100 text-green-700';
    }
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset page when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: prescriptionsResponse,
    isLoading: isPrescriptionsLoading,
    refetch: refetchPrescriptions,
  } = useQuery({
    queryKey: ['prescriptions', patientId, page, debouncedSearch, sortField, sortOrder],
    queryFn: async () => {
      const response = await prescriptionService.getPrescriptionsByPatientId(patientId, {
        page,
        sortBy: sortField,
        sortOrder: sortOrder.toUpperCase(),
        search: debouncedSearch,
      });
      return response;
    },
    enabled: !!patientId,
  });

  const prescriptions = prescriptionsResponse?.data?.prescriptions || [];
  const totalItems = prescriptionsResponse?.data?.pagination?.totalItems ?? 0;
  const totalPages = prescriptionsResponse?.data?.pagination?.totalPages ?? 1;

  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return { color: 'bg-emerald-100 text-emerald-700', label: 'Active' };
      case 'verified':
        return { color: 'bg-purple-100 text-[rgb(99,57,144)]', label: 'Verified' };
      case 'sent_to_pharmacy':
        return { color: 'bg-blue-100 text-blue-700', label: 'Sent to Pharmacy' };
      case 'pending':
        return { color: 'bg-amber-100 text-amber-700', label: 'Pending Review' };
      case 'delivered':
        return { color: 'bg-blue-100 text-blue-700', label: 'Delivered' };
      case 'filled':
        return { color: 'bg-orange-100 text-orange-700', label: 'Being Filled' };
      case 'ready':
        return { color: 'bg-cyan-100 text-cyan-700', label: 'Ready for Shipment' };
      case 'shipped':
        return { color: 'bg-indigo-100 text-indigo-700', label: 'Shipped' };
      case 'cancelled':
        return { color: 'bg-red-600 text-white', label: 'Cancelled' };
      default:
        return { color: 'bg-slate-100 text-slate-600', label: status };
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePrescriptionClick = (prescription: Prescription) => {
    setSelectedPrescription(prescription);
    setIsDetailSheetOpen(true);
  };

  const handleStatusUpdate = (prescriptionId: string, status: Prescription['status']) => {
    refetchPrescriptions();
    // Update selected prescription if it's the one being updated
    if (selectedPrescription?.id === prescriptionId) {
      setSelectedPrescription(prev => prev ? { ...prev, status } : null);
    }
  };

  const handleActionComplete = async () => {
    // Refetch prescriptions to get updated data
    await refetchPrescriptions();
    // Close the detail sheet
    setIsDetailSheetOpen(false);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Main Card */}
        <div className="bg-white rounded-lg border border-slate-200">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#4A8A9A' }}
                >
                  <Pill className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Orders</h3>
                  <p className="text-xs text-slate-500">
                    {isPrescriptionsLoading ? 'Loading...' : `${totalItems} total orders`}
                  </p>
                </div>
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by medication, RX number..."
                  className="pl-9 h-9 text-sm"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value.trimStart())}
                />
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-slate-500">Sort by:</span>
              <button
                onClick={() => handleSort('created_at')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${sortField === 'created_at' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'
                  }`}
              >
                Date
                <SortIcon field="created_at" />
              </button>
              <button
                onClick={() => handleSort('status')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${sortField === 'status' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'
                  }`}
              >
                Status
                <SortIcon field="status" />
              </button>
              <button
                onClick={() => handleSort('priority')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${sortField === 'priority' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'
                  }`}
              >
                Priority
                <SortIcon field="priority" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {isPrescriptionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[#4A8A9A]" />
                <span className="ml-2 text-sm text-slate-500">Loading orders...</span>
              </div>
            ) : prescriptions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Pill className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500">No orders found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {prescriptions.map((prescription) => {
                  const statusConfig = getStatusConfig(prescription.status);
                  return (
                    <div
                      key={prescription.id}
                      className="border border-slate-200 rounded-lg p-4 bg-white"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        {/* Medication Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: 'rgba(99, 57, 144, 0.12)' }}
                            >
                              <Package className="h-5 w-5" style={{ color: 'rgb(99, 57, 144)' }} />
                            </div>
                            <div className="min-w-0 flex-1">
                              {prescription.medicationRequests?.map((medication, idx) => (
                                <div key={idx} className="font-medium text-slate-900 text-sm">
                                  {medication.medication}
                                  {medication.rx_number && (
                                    <span className="text-xs text-blue-600 ml-1.5 font-normal">(RX: {medication.rx_number})</span>
                                  )}
                                  <span className="text-slate-500 font-normal">
                                    {' '}• {medication.dose} {medicationUnits[medication.unit_of_measure] || medication.unit_of_measure}
                                  </span>
                                </div>
                              ))}
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {prescription.practitioner?.full_name ?? 'Unknown'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatToLocalDate(prescription.created_at, 'MM/DD/YYYY hh:mm a')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Status & Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                          {prescription.is_external_fulfillment && prescription.status !== 'pending' && prescription.status !== 'verified' ? (
                            <Badge className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                              prescription.external_fulfillment_status === 'sent'
                                ? 'bg-teal-50 text-teal-700 border border-teal-200'
                                : prescription.external_fulfillment_status === 'shipped'
                                ? 'bg-sky-50 text-sky-700 border border-sky-200'
                                : prescription.external_fulfillment_status === 'delivered'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-violet-50 text-violet-700 border border-violet-200'
                            }`}>
                              {prescription.external_fulfillment_status === 'sent'
                                ? 'Sent to External Pharmacy'
                                : prescription.external_fulfillment_status === 'shipped'
                                ? 'Shipped (External)'
                                : prescription.external_fulfillment_status === 'delivered'
                                ? 'Delivered (External)'
                                : 'Queued for External Fulfillment'}
                            </Badge>
                          ) : (
                            <Badge className={`${statusConfig.color} text-xs font-medium px-2.5 py-1 rounded-full hover:${statusConfig.color}`}>
                              {statusConfig.label}
                            </Badge>
                          )}
                          {prescription.payment_status === 'paid' && (
                            <Badge className="bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full hover:bg-green-100">
                              Paid
                            </Badge>
                          )}
                          {prescription.payment_status === 'refunded' && (
                            <Badge className="bg-slate-500 text-white text-xs font-medium px-2.5 py-1 rounded-full hover:bg-slate-100">
                              Refunded
                            </Badge>
                          )}
                          {/* Cancellation Rejection Status Indicator */}
                          {prescription.cancellationRequests?.some(req => req.status === 'rejected') && (
                            <Badge className="bg-red-50 text-red-700 border border-red-200 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                              <XCircle className="h-3 w-3" />
                              Cancellation Rejected
                            </Badge>
                          )}
                          {/* Pending Cancellation Request Indicator */}
                          {prescription.cancellationRequests?.some(req => req.status === 'pending') && (
                            <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Cancellation Pending
                            </Badge>
                          )}
                          <Badge className={`${getPriorityColor(prescription.priority ?? 1)} text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1`}>
                            <Flag className="h-3 w-3" />
                            {getPriorityText(prescription.priority ?? 1)}
                          </Badge>
                          {prescription.medicationRequests?.[0] && (
                            <button
                              type="button"
                              onClick={() => {
                                const med = prescription.medicationRequests?.[0];
                                setSafetyDraft({
                                  medication: med?.medication,
                                  dose: med?.dose ? String(med.dose) : undefined,
                                  quantity: med?.quantity_authorized
                                    ? String(med.quantity_authorized)
                                    : undefined,
                                  refills: med?.refills_authorized
                                    ? String(med.refills_authorized)
                                    : undefined,
                                });
                                setSafetyOpen(true);
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-white px-2.5 py-1 text-xs font-medium text-teal-700 hover:bg-teal-50"
                              title="Run IntelliHealth safety check"
                              aria-label="Safety check"
                            >
                              <ShieldCheck className="h-3 w-3" />
                              Safety
                            </button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-slate-100"
                              >
                                <MoreHorizontal className="h-4 w-4 text-slate-500" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                onClick={() => handlePrescriptionClick(prescription)}
                                className="cursor-pointer"
                                style={{ fontSize: '13px' }}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>

                              {(['pending', 'verified'].includes(prescription.status)) && prescription.payment_status !== 'paid' && isPractitioner && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedPrescription(prescription);
                                      setIsEditPrescriptionOpen(true);
                                    }}
                                    className="cursor-pointer text-slate-700"
                                    style={{ fontSize: '13px' }}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Edit Order
                                  </DropdownMenuItem>
                                </>
                              )}

                              {prescription.status === 'pending' && isPractitioner && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedPrescription(prescription);
                                      setIsVerificationConfirmationOpen(true);
                                    }}
                                    className="cursor-pointer text-brand-teal"
                                    style={{ fontSize: '13px' }}
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Verify
                                  </DropdownMenuItem>
                                </>
                              )}

                              {prescription.status === 'verified' &&
                                prescription.payment_status === 'paid' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={async () => {
                                        try {
                                          const result = await prescriptionService.submitPrescription(
                                            prescription.id
                                          );
                                          refetchPrescriptions();
                                          toast({
                                            variant: 'success',
                                            title: (result as any).isExternalFulfillment ? 'Queued for external fulfillment' : 'Order submitted to pharmacy successfully',
                                          });
                                        } catch (error: any) {
                                          const errorMessage =
                                            error?.message ??
                                            error?.response?.data?.error?.message ??
                                            error?.response?.data?.message ??
                                            'Please try again later';
                                          toast({
                                            variant: 'destructive',
                                            title: 'Order submission failed',
                                            description: errorMessage,
                                          });
                                        }
                                      }}
                                      className="cursor-pointer text-brand-copper"
                                      style={{ fontSize: '13px' }}
                                    >
                                      <Send className="mr-2 h-4 w-4" />
                                      Submit to Pharmacy
                                    </DropdownMenuItem>
                                  </>
                                )}

                              {(prescription.status === 'pending' ||
                                prescription.status === 'verified') &&
                                prescription.payment_status === 'pending' &&
                                permissionCheck(
                                  'payments:collect_payment',
                                  rolePermissions,
                                  isPractitioner || isAdmin
                                ) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedPrescription(prescription);
                                        setIsPaymentSheetOpen(true);
                                      }}
                                      className="cursor-pointer text-emerald-600"
                                      style={{ fontSize: '13px' }}
                                    >
                                      <CreditCard className="mr-2 h-4 w-4" />
                                      Process Payment
                                    </DropdownMenuItem>
                                    {getDefaultContactValue(prescription.patient?.contacts, 'email') && (
                                        <DropdownMenuItem
                                          onClick={() => {
                                            setIsSendLinkSheetOpen({ open: true, prescription });
                                          }}
                                          className="cursor-pointer text-brand-copper"
                                          style={{ fontSize: '13px' }}
                                        >
                                          <Send className="mr-2 h-4 w-4" />
                                          Send Payment Link
                                        </DropdownMenuItem>
                                      )}
                                  </>
                                )}

                              {/* Cancel Prescription Option - hide if cancellation request already exists or linked to guided order */}
                              {prescription.status !== 'shipped' &&
                                prescription.status !== 'delivered' &&
                                prescription.status !== 'cancelled' &&
                                (!prescription.cancellationRequests ||
                                  prescription.cancellationRequests.length === 0) &&
                                !(prescription as any).guided_order_id &&
                                (isPractitioner || isAdmin) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedPrescription(prescription);
                                        setIsCancelDialogOpen(true);
                                      }}
                                      className="cursor-pointer text-red-600"
                                      style={{ fontSize: '13px' }}
                                    >
                                      <AlertCircle className="mr-2 h-4 w-4" />
                                      Cancel Order
                                    </DropdownMenuItem>
                                  </>
                                )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="px-5 py-4 border-t border-slate-100">
              <SmartPagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Prescription Detail Sheet */}
      {selectedPrescription && (
        <PrescriptionDetailSheet
          open={isDetailSheetOpen}
          onOpenChange={setIsDetailSheetOpen}
          prescription={selectedPrescription}
          onStatusUpdate={handleStatusUpdate}
          onActionComplete={handleActionComplete}
        />
      )}

      {/* Verification Confirmation Dialog */}
      {selectedPrescription && (
        <VerificationConfirmationDialog
          open={isVerificationConfirmationOpen}
          onOpenChange={setIsVerificationConfirmationOpen}
          prescriptionId={selectedPrescription.id}
          patientName={selectedPrescription.patient?.full_name}
          practitionerName={selectedPrescription.practitioner?.full_name}
          onConfirm={async () => {
            try {
              setIsVerificationProcessing(true);
              await prescriptionService.verifyPrescription(selectedPrescription.id);
              if (selectedPrescription.payment_status === 'paid') {
                await prescriptionService.submitPrescription(selectedPrescription.id);
              }
              refetchPrescriptions();
              toast({
                title: 'Success',
                description: 'Order verified successfully',
                variant: 'success',
              });
            } catch (error: any) {
              console.error('Error verifying prescription:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to verify order',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsVerificationProcessing(false);
            }
          }}
          isLoading={isVerificationProcessing}
        />
      )}

      {/* Payment Sheet */}
      {selectedPrescription && isPaymentSheetOpen && (
        <PaymentSheet
          open={isPaymentSheetOpen}
          onOpenChange={setIsPaymentSheetOpen}
          prescriptionId={selectedPrescription.id}
          onPaymentComplete={async () => {
            try {
              setIsPaymentProcessing(true);
              if (selectedPrescription.status === 'verified') {
                await prescriptionService.submitPrescription(selectedPrescription.id);
              }
              refetchPrescriptions();
              toast({
                title: 'Success',
                description: 'Payment processed and order placed successfully',
                variant: 'success',
              });
              setIsPaymentSheetOpen(false);
            } catch (error: any) {
              console.error('Error processing payment:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to process payment',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsPaymentProcessing(false);
            }
          }}
          onPrescriptionUpdated={() => {
            refetchPrescriptions(); // Refetch list when medications are removed
          }}
        />
      )}

      {/* Send Link Dialog */}
      {isSendLinkSheetOpen.open && isSendLinkSheetOpen.prescription && (
        <SendPaymentLinkDialog
          open={isSendLinkSheetOpen.open}
          onOpenChange={() => setIsSendLinkSheetOpen({ open: false, prescription: null })}
          prescription_id={isSendLinkSheetOpen.prescription.id}
          onSuccess={() => {
            refetchPrescriptions();
            setIsSendLinkSheetOpen({ open: false, prescription: null });
          }}
          onPrescriptionUpdated={() => {
            refetchPrescriptions(); // Refetch list when medications are removed
          }}
        />
      )}

      {/* Cancel Prescription Dialog */}
      {selectedPrescription && (
        <CancelPrescriptionDialog
          open={isCancelDialogOpen}
          onOpenChange={setIsCancelDialogOpen}
          prescription={selectedPrescription}
          onSuccess={() => {
            refetchPrescriptions();
            setIsCancelDialogOpen(false);
          }}
        />
      )}

      {/* Edit Prescription */}
      {selectedPrescription && (
        <PrescriptionFormSheet
          open={isEditPrescriptionOpen}
          onOpenChange={setIsEditPrescriptionOpen}
          mode="edit"
          initialData={selectedPrescription}
          patientId={selectedPrescription.patient?.id || ''}
          practitionerId={selectedPrescription.practitioner?.id || ''}
          onSubmit={() => {
            refetchPrescriptions();
          }}
        />
      )}

      <RxSafetyDialog
        open={safetyOpen}
        onOpenChange={setSafetyOpen}
        patient={safetyPatient}
        prescriptionDraft={safetyDraft}
      />
    </>
  );
};

export default PatientPrescriptionsTab;
